import { useMemo } from "react";
import { PestItem } from "@/services/backend/qpm/pests/types/pest";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { Skeleton } from "@/shared/components/ui/skeleton";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/shared/components/ui/hover-card";
import { Button } from "@/shared/components/ui/button";
import { AlertTriangleIcon, Trash2 } from "lucide-react";
import t from "@/lang/lang";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/shared/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { useQpmPestListQuery } from "@/services/backend/qpm/pests/api/pest-api";
import { PestKind } from "@/services/backend/qpm/pests/types/pest-enum";
import { MonitorPestView } from "@/services/backend/qpm/monitors/types/monitor-view";
import { useActiveCompany } from "@/shared/lib/active-company/active-company-provider";

type PestSelectorProps = {
  kind: PestKind;
  selectedItems: MonitorPestView[];
  onSelect: (selectedItem: MonitorPestView) => void;
  disabled?: boolean;
};

export function PestSelectorComponent({
  kind,
  selectedItems,
  onSelect,
  disabled,
}: PestSelectorProps) {
  const { companyId } = useActiveCompany();
  const {
    data: list,
    error,
    isLoading,
  } = useQpmPestListQuery({
    company: { active: true, values: [companyId] },
    kind,
  });

  const availableItems = useMemo(() => {
    const selectedIds = new Set(
      selectedItems.filter((p) => p.kind === kind).map((p) => p.id),
    );

    return (
      list?.items
        ?.filter((p) => !selectedIds.has(p.id))
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name)) ?? []
    );
  }, [list?.items, selectedItems, kind]);

  if (error) return <RTKQueryErrorAlert error={error} />;

  if (isLoading) {
    return (
      <div>
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  if (!list || (list.items.length === 0 && selectedItems.length === 0)) {
    return (
      <HoverCard>
        <HoverCardTrigger>
          <Button
            variant="outline"
            className="text-muted-foreground border-yellow-300"
          >
            <AlertTriangleIcon className="h-4 w-4 text-yellow-300" />
            {t("Hinweis")}
          </Button>
        </HoverCardTrigger>
        <HoverCardContent>
          {t(
            "Es wurden noch keine Stammdaten für die Schädlingskategorien festgelegt!",
          )}
          <br />
          {t(
            "Bitte erstellen Sie zunächst die Schädlingskategorien in den Stammdaten.",
          )}
        </HoverCardContent>
      </HoverCard>
    );
  }

  const label =
    kind === PestKind.Category
      ? t("Schädlings-Kategorie hinzufügen")
      : t("Schädlings-Indikator hinzufügen");

  return (
    <Select
      onValueChange={(id: string) => {
        const item = availableItems.find((v) => v.id === id);
        if (!item) return;

        onSelect({
          id: item.id,
          kind,
          name: item.name,
        });
      }}
      disabled={disabled}
    >
      <SelectTrigger className="w-[300px]">{label}</SelectTrigger>
      <SelectContent align="end">
        {availableItems.map((item) => (
          <SelectItem key={item.id} value={item.id}>
            {item.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

type SelectedPestsTableProps = {
  kind: PestKind;
  selectedItems: MonitorPestView[];
  onDeleteSelected?: (selectedId: string) => void;
  disabled?: boolean;
};

export function SelectedPestsTable({
  kind,
  selectedItems,
  onDeleteSelected,
  disabled,
}: SelectedPestsTableProps) {
  const { companyId } = useActiveCompany();
  const {
    data: list,
    error,
    isLoading,
  } = useQpmPestListQuery({
    company: { active: true, values: [companyId] },
    kind,
  });

  const selectedIds = useMemo(
    () =>
      new Set(selectedItems.filter((p) => p.kind === kind).map((p) => p.id)),
    [selectedItems, kind],
  );

  const rows: PestItem[] = useMemo(() => {
    const items = list?.items ?? [];
    return items
      .filter((p) => selectedIds.has(p.id))
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [list?.items, selectedIds]);

  if (error) return <RTKQueryErrorAlert error={error} />;

  if (isLoading) {
    return (
      <div>
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  const heading =
    kind === PestKind.Category ? t("Kategorien") : t("Indikatoren");

  return (
    <div className="flex flex-col gap-2">
      <div className="text-sm font-medium">{heading}</div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[35%]">{t("Name")}</TableHead>
            <TableHead className="w-[20%]">{t("Abkürzung")}</TableHead>
            <TableHead className="w-[35%]">{t("Beschreibung")}</TableHead>
            {onDeleteSelected && (
              <TableHead className="w-[10%] text-right">
                {t("Aktion")}
              </TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((item) => (
            <TableRow key={item.id}>
              <TableCell className="py-1 font-medium">{item.name}</TableCell>
              <TableCell className="py-1">{item.abbreviation || "-"}</TableCell>
              <TableCell className="py-1">{item.description || "-"}</TableCell>
              {onDeleteSelected && (
                <TableCell className="py-1 text-end">
                  <div className="flex items-center justify-end">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            className="h-fit p-1"
                            size="sm"
                            variant="outline"
                            onClick={() => onDeleteSelected(item.id)}
                            disabled={disabled}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{t("Entfernen")}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </TableCell>
              )}
            </TableRow>
          ))}

          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={4} className="text-center">
                -- {t("Noch keine Auswahl")} --
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
